import { computed, ref } from '@vue/composition-api'
import Vue from 'vue'
import store from '../../admin-portal/src/state/store.js'
import { getAdminPortalNavbarItems } from '../utils/api/admin-portal-navigation.js'
import { SEE_COOKIE_MODULE_AREA, CAN_SEE_CONSENT_COLLECTIONS } from '../permissions/admin-portal-permissions.js'
import { dashboardModuleEnabled, prefPageAnalyticsEnabled, privacyNoticeModuleEnabled, enableConsentCollectionPreferencePageOptions, enableConsentCollectionWidgetOptions, waitForLoad, consentConfigApiEnabled, siteScannerModuleEnabled } from './config-keys.js'
import { COOKIES_NAV_ID, SITE_SCANNER_NAV_CUSTOM_ACTION, redirectSiteScannerNavItem } from '../utils/navigation/site-scanner-nav-item.js'

const navBarItemsState = ref(null)
const externalLinksState = ref([])
const labels = ref([])
let isLoading = false
let isLoadingPromise = null

const ADMINISTRATION_NAV_ID = 2
const PRIVACY_POLICIES_NAV_ID = 9
const EXPORTS_NAV_ID = 44
const FILE_LOADER_NAV_ID = 86
const SYNTHETIC_DASHBOARD_KEY = 'synthetic-dashboard-new'
const SYNTHETIC_BULK_DATA_KEY = 'synthetic-bulk-data'
export const DASHBOARD_NAV_CUSTOM_ACTION = 'dashboard-portal'
export const PRIVACY_NOTICE_NAV_CUSTOM_ACTION = 'privacy-notice-portal'
const CONSENT_COLLECTION_NAV_ID = 82
const SYNTHETIC_PREFPAGE_ANALYTICS_KEY = 'synthetic-prefpage-analytics-dashboard'
export const PREFPAGE_ANALYTICS_NAV_CUSTOM_ACTION = 'prefpage-analytics-portal'
const SYNTHETIC_CONSENT_CONFIG_KEY = 'synthetic-consent-config'
export const CONSENT_CONFIGURATION_API_NAV_CUSTOM_ACTION = 'consent-configuration-api-portal'
// Re-exported so app.vue can keep importing every custom-action discriminator
// from one place. The constant itself lives with the pure transform.
export { SITE_SCANNER_NAV_CUSTOM_ACTION }
// Consent Collection child nav IDs gated by ConfigKeys
const CUSTOM_PREF_PAGES_NAV_ID = 38
const DND_PREF_PAGES_NAV_ID = 87
const EMBEDDED_CONSENT_NAV_ID = 35
const CC_WIDGET_NAV_ID = 88

const cloneNavigation = navigation => navigation.map(topLevel => ({
	...topLevel,
	sublinks: Array.isArray(topLevel.sublinks)
		? topLevel.sublinks.map(subLink => ({
			...subLink,
			deeperSublinks: Array.isArray(subLink.deeperSublinks) ? [...subLink.deeperSublinks] : []
		}))
		: []
}))

// Support/training link text values to move to user menu (case-insensitive)
// These are partial matches - if the text contains any of these, it's considered a support/training link
const SUPPORT_TRAINING_KEYWORDS = [
	'support',
	'training',
	'help',
	'learning hub'
]

// Check if a nav item is a support/training link that should be moved to user menu
const isSupportTrainingLink = item => {
	const text = (item.text || '').toLowerCase().trim()
	// Move Support and Training related links to user menu
	return SUPPORT_TRAINING_KEYWORDS.some(keyword => text.includes(keyword))
}

// Extract support/training links and remove them from navigation
const extractExternalLinks = navigation => {
	const externalLinks = []

	// Filter out support/training items at each level
	const filteredNavigation = navigation
		.filter(topLevel => {
			if (isSupportTrainingLink(topLevel)) {
				externalLinks.push({ ...topLevel, level: 'top' })
				return false
			}
			return true
		})
		.map(topLevel => ({
			...topLevel,
			sublinks: Array.isArray(topLevel.sublinks)
				? topLevel.sublinks
					.filter(subLink => {
						if (isSupportTrainingLink(subLink)) {
							externalLinks.push({ ...subLink, level: 'sub' })
							return false
						}
						return true
					})
					.map(subLink => ({
						...subLink,
						deeperSublinks: Array.isArray(subLink.deeperSublinks)
							? subLink.deeperSublinks.filter(deeperLink => {
								if (isSupportTrainingLink(deeperLink)) {
									externalLinks.push({ ...deeperLink, level: 'deeper' })
									return false
								}
								return true
							})
							: []
					}))
				: []
		}))

	return { filteredNavigation, externalLinks }
}

const buildLabels = navigation => ([
	...navigation
		.filter(({ displayText }) => displayText != null)
		.map(({ displayText, text }) => ({ displayText, text })),
	...navigation
		.map(({ sublinks }) => (sublinks || [])
			.filter(({ displayText }) => displayText != null)
			.map(({ displayText, text }) => ({ displayText, text }))).flat(),
	...navigation
		.map(({ sublinks }) => (sublinks || [])
			.map(({ deeperSublinks }) => (deeperSublinks || [])
				.filter(({ displayText }) => displayText != null)
				.map(({ displayText, text }) => ({ displayText, text }))).flat()).flat()
])

const userHasCookieModulePermission = () => {
	const permissionGetter = store.getters?.['auth/productAreaPermission']
	if (typeof permissionGetter !== 'function') return false
	return permissionGetter(SEE_COOKIE_MODULE_AREA)
}

const userHasConsentCollectionPermission = () => {
	const permissionGetter = store.getters?.['auth/productAreaPermission']
	if (typeof permissionGetter !== 'function') return false
	return permissionGetter(CAN_SEE_CONSENT_COLLECTIONS)
}

const isUserLoggedIn = () => Boolean(store.getters?.['auth/loggedIn'])

const removeSyntheticDashboardChild = parent => {
	if (!Array.isArray(parent.sublinks)) {
		parent.sublinks = []
		return
	}
	parent.sublinks = parent.sublinks.filter(child => child.syntheticKey !== SYNTHETIC_DASHBOARD_KEY)
}

const maybeInsertDashboardNavItem = navigation => {
	const cookiesParent = navigation.find(item => item.adminPortalNavigationId === COOKIES_NAV_ID || item.text === 'Cookies')
	if (!cookiesParent) return
	removeSyntheticDashboardChild(cookiesParent)
	if (!dashboardModuleEnabled.value || !isUserLoggedIn() || !userHasCookieModulePermission()) {
		return
	}
	const alreadyExists = cookiesParent.sublinks?.some(child => child.syntheticKey === SYNTHETIC_DASHBOARD_KEY || child.customActionType === DASHBOARD_NAV_CUSTOM_ACTION)
	if (alreadyExists) {
		return
	}
	const newDashboardNavItem = {
		adminPortalNavigationId: SYNTHETIC_DASHBOARD_KEY,
		text: 'Dashboard',
		displayText: 'Dashboard',
		icon: 'mdi-chart-bar',
		isExternal: false,
		route: '',
		deeperSublinks: [],
		customActionType: DASHBOARD_NAV_CUSTOM_ACTION,
		syntheticKey: SYNTHETIC_DASHBOARD_KEY,
		chipLabel: 'NEW'
	}
	cookiesParent.sublinks = Array.isArray(cookiesParent.sublinks)
		? [newDashboardNavItem, ...cookiesParent.sublinks]
		: [newDashboardNavItem]
}

const removeSyntheticPrefPageAnalyticsChild = parent => {
	if (!Array.isArray(parent.sublinks)) {
		parent.sublinks = []
		return
	}
	parent.sublinks = parent.sublinks.filter(child => child.syntheticKey !== SYNTHETIC_PREFPAGE_ANALYTICS_KEY)
}

const maybeInsertPrefPageAnalyticsDashboardNavItem = navigation => {
	const consentCollectionParent = navigation.find(item => item.adminPortalNavigationId === CONSENT_COLLECTION_NAV_ID || item.text === 'Consent Collection')
	if (!consentCollectionParent) return
	removeSyntheticPrefPageAnalyticsChild(consentCollectionParent)
	if (!prefPageAnalyticsEnabled.value || !isUserLoggedIn() || !userHasConsentCollectionPermission()) {
		return
	}
	const alreadyExists = consentCollectionParent.sublinks?.some(child =>
		child.syntheticKey === SYNTHETIC_PREFPAGE_ANALYTICS_KEY || child.customActionType === PREFPAGE_ANALYTICS_NAV_CUSTOM_ACTION)
	if (alreadyExists) {
		return
	}
	const newPrefPageAnalyticsNavItem = {
		adminPortalNavigationId: SYNTHETIC_PREFPAGE_ANALYTICS_KEY,
		text: 'Dashboard',
		displayText: 'Dashboard',
		icon: 'mdi-chart-bar',
		isExternal: false,
		route: '',
		deeperSublinks: [],
		customActionType: PREFPAGE_ANALYTICS_NAV_CUSTOM_ACTION,
		syntheticKey: SYNTHETIC_PREFPAGE_ANALYTICS_KEY,
		chipLabel: 'NEW'
	}
	consentCollectionParent.sublinks = Array.isArray(consentCollectionParent.sublinks)
		? [newPrefPageAnalyticsNavItem, ...consentCollectionParent.sublinks]
		: [newPrefPageAnalyticsNavItem]
}

const removeSyntheticConsentConfigApiChild = parent => {
	if (!Array.isArray(parent.sublinks)) {
		parent.sublinks = []
		return
	}
	parent.sublinks = parent.sublinks.filter(child => child.syntheticKey !== SYNTHETIC_CONSENT_CONFIG_KEY)
}

const maybeInsertConsentConfigApiNavItem = navigation => {
	const consentCollectionParent = navigation.find(
		item => item.adminPortalNavigationId === CONSENT_COLLECTION_NAV_ID || item.text === 'Consent Collection'
	)
	if (!consentCollectionParent) return
	removeSyntheticConsentConfigApiChild(consentCollectionParent)
	if (!consentConfigApiEnabled.value || !isUserLoggedIn() || !userHasConsentCollectionPermission()) {
		return
	}
	const alreadyExists = consentCollectionParent.sublinks?.some(child =>
		child.syntheticKey === SYNTHETIC_CONSENT_CONFIG_KEY ||
		child.customActionType === CONSENT_CONFIGURATION_API_NAV_CUSTOM_ACTION
	)
	if (alreadyExists) return
	const newItem = {
		adminPortalNavigationId: SYNTHETIC_CONSENT_CONFIG_KEY,
		text: 'Consent Configuration API',
		displayText: 'Consent Configuration API',
		icon: 'mdi-api',
		isExternal: false,
		route: '',
		deeperSublinks: [],
		customActionType: CONSENT_CONFIGURATION_API_NAV_CUSTOM_ACTION,
		syntheticKey: SYNTHETIC_CONSENT_CONFIG_KEY
	}
	consentCollectionParent.sublinks = Array.isArray(consentCollectionParent.sublinks)
		? [...consentCollectionParent.sublinks, newItem]
		: [newItem]
}

/**
 * Filter Consent Collection child nav items based on ConfigKeys.
 * ConsentCollectionPreferencePageOptions: 0 = legacy only (38), 1 = D&D only (87), 2 = both
 * ConsentCollectionWidgetOptions: 0 = embedded only (35), 1 = widget only (88), 2 = both
 */
const maybeFilterConsentCollectionByConfigKeys = navigation => {
	const ccParent = navigation.find(
		item => item.adminPortalNavigationId === CONSENT_COLLECTION_NAV_ID || item.text === 'Consent Collection'
	)
	if (!ccParent || !Array.isArray(ccParent.sublinks)) return

	const prefPageOption = enableConsentCollectionPreferencePageOptions.value
	const widgetOption = enableConsentCollectionWidgetOptions.value

	const idsToHide = new Set()

	// Preference page options: 0 = legacy only, 1 = D&D only, 2 = both
	if (prefPageOption === '0') idsToHide.add(DND_PREF_PAGES_NAV_ID)
	else if (prefPageOption === '1') idsToHide.add(CUSTOM_PREF_PAGES_NAV_ID)
	// '2' or undefined = show both

	// Widget options: 0 = embedded only, 1 = widget only, 2 = both
	if (widgetOption === '0') idsToHide.add(CC_WIDGET_NAV_ID)
	else if (widgetOption === '1') idsToHide.add(EMBEDDED_CONSENT_NAV_ID)
	// '2' or undefined = show both

	if (idsToHide.size > 0) {
		ccParent.sublinks = ccParent.sublinks.filter(
			child => !idsToHide.has(child.adminPortalNavigationId)
		)
	}
}

const maybeRedirectPrivacyPoliciesNavItem = navigation => {
	if (!privacyNoticeModuleEnabled.value || !isUserLoggedIn()) {
		return
	}
	const administrationParent = navigation.find(item => item.adminPortalNavigationId === ADMINISTRATION_NAV_ID || item.text === 'Administration')
	if (!administrationParent || !Array.isArray(administrationParent.sublinks)) return
	const privacyPoliciesItem = administrationParent.sublinks.find(
		child => child.adminPortalNavigationId === PRIVACY_POLICIES_NAV_ID || child.text === 'Privacy Policies'
	)
	if (!privacyPoliciesItem) return
	privacyPoliciesItem.customActionType = PRIVACY_NOTICE_NAV_CUSTOM_ACTION
	privacyPoliciesItem.chipLabel = 'NEW'
}

/**
 * Point the Site Scanner menu entry at the new scanner instead of the old Vue
 * view. Gated on the same config key that unlocked the previous release's
 * opt-in banner, so a tenant still on the old scanner is untouched.
 *
 * The login guard matters: the transform is reached on the pre-auth routes too,
 * and the custom action it installs fetches an exchange token.
 */
const maybeRedirectSiteScannerNavItem = navigation => {
	if (!siteScannerModuleEnabled.value || !isUserLoggedIn()) {
		return
	}
	redirectSiteScannerNavItem(navigation)
}

/**
 * Check if a navigation item is the Exports nav item
 * @param {object} item - Navigation item to check
 * @returns {boolean}
 */
const isExportsNavItem = item =>
	item.adminPortalNavigationId === EXPORTS_NAV_ID ||
	(item.route && typeof item.route === 'object' && item.route.name === 'data-exports') ||
	item.route === 'data-exports'

/**
 * Check if a navigation item is the File Loader nav item
 * @param {object} item - Navigation item to check
 * @returns {boolean}
 */
const isFileLoaderNavItem = item =>
	item.adminPortalNavigationId === FILE_LOADER_NAV_ID ||
	(item.route && typeof item.route === 'object' && item.route.name === 'file-loader') ||
	item.route === 'file-loader'

/**
 * Find and remove Exports item from navigation, returning it and its position if found
 * @param {Array} navigation - Top-level navigation items
 * @returns {{ item: object, index: number } | null}
 */
const findAndRemoveExportsItem = navigation => {
	// Check top level
	const topLevelIndex = navigation.findIndex(isExportsNavItem)
	if (topLevelIndex !== -1) {
		const item = navigation.splice(topLevelIndex, 1)[0]
		return { item, index: topLevelIndex }
	}

	// Check sublinks of each top-level item
	for (let i = 0; i < navigation.length; i++) {
		const topLevel = navigation[i]
		if (!Array.isArray(topLevel.sublinks)) continue
		const sublinkIndex = topLevel.sublinks.findIndex(isExportsNavItem)
		if (sublinkIndex !== -1) {
			const item = topLevel.sublinks.splice(sublinkIndex, 1)[0]
			// Insert Bulk Data at top level, after the parent item's position
			return { item, index: i + 1 }
		}

		// Check deeperSublinks using indexed loop
		for (let j = 0; j < topLevel.sublinks.length; j++) {
			const sublink = topLevel.sublinks[j]
			if (!Array.isArray(sublink.deeperSublinks)) continue
			const deeperIndex = sublink.deeperSublinks.findIndex(isExportsNavItem)
			if (deeperIndex !== -1) {
				const item = sublink.deeperSublinks.splice(deeperIndex, 1)[0]
				// Insert Bulk Data at top level, after the parent item's position
				return { item, index: i + 1 }
			}
		}
	}

	return null
}

/**
 * Transform Exports and File Loader into a Bulk Data parent with both as sublinks
 * File Loader is an independent top-level nav item (not nested under Exports)
 * Mutates the navigation array in place
 * @param {Array} navigation - Top-level navigation items
 */
const maybeTransformExportsToBulkData = navigation => {
	if (!Array.isArray(navigation)) return

	// Check if Bulk Data already exists (avoid duplicates)
	const bulkDataExists = navigation.some(item => item.syntheticKey === SYNTHETIC_BULK_DATA_KEY)
	if (bulkDataExists) return

	// Find and remove Exports item
	const exportsResult = findAndRemoveExportsItem(navigation)

	// Find and remove File Loader from top level (independent nav item)
	const fileLoaderIndex = navigation.findIndex(isFileLoaderNavItem)
	let fileLoaderItem = fileLoaderIndex !== -1
		? navigation.splice(fileLoaderIndex, 1)[0]
		: null

	// Backwards compatibility: also check Exports' sublinks for File Loader
	if (!fileLoaderItem && exportsResult) {
		const exportsSublinks = Array.isArray(exportsResult.item.sublinks) ? exportsResult.item.sublinks : []
		const nestedIndex = exportsSublinks.findIndex(sub =>
			sub.adminPortalNavigationId === FILE_LOADER_NAV_ID ||
			sub.route?.name === 'file-loader'
		)
		if (nestedIndex !== -1) {
			fileLoaderItem = exportsSublinks.splice(nestedIndex, 1)[0]
		}
	}

	// Need at least one item to create Bulk Data
	if (!exportsResult && !fileLoaderItem) return

	const bulkDataSublinks = []
	const insertIndex = exportsResult ? exportsResult.index : fileLoaderIndex

	if (exportsResult) {
		bulkDataSublinks.push({
			...exportsResult.item,
			deeperSublinks: Array.isArray(exportsResult.item.deeperSublinks) ? exportsResult.item.deeperSublinks : []
		})
	}

	if (fileLoaderItem) {
		bulkDataSublinks.push({
			...fileLoaderItem,
			deeperSublinks: Array.isArray(fileLoaderItem.deeperSublinks) ? fileLoaderItem.deeperSublinks : []
		})
	}

	// Create Bulk Data top-level item
	const bulkDataItem = {
		adminPortalNavigationId: SYNTHETIC_BULK_DATA_KEY,
		text: 'Bulk Data',
		displayText: 'Bulk Data',
		icon: 'mdi-swap-horizontal',
		isExternal: false,
		route: '',
		syntheticKey: SYNTHETIC_BULK_DATA_KEY,
		sublinks: bulkDataSublinks
	}

	navigation.splice(insertIndex, 0, bulkDataItem)
}

const load = async () => {
	if (isLoading) return
	isLoading = true
	isLoadingPromise = (async () => {
		try {
			const { adminPortalNavigation } = await getAdminPortalNavbarItems()
			await waitForLoad()
			const clonedNavigation = cloneNavigation(adminPortalNavigation)

			// Extract external links (support/training) before other processing
			const { filteredNavigation, externalLinks } = extractExternalLinks(clonedNavigation)
			externalLinksState.value = externalLinks

			// Continue with normal nav processing on filtered navigation
			maybeInsertDashboardNavItem(filteredNavigation)
			maybeInsertPrefPageAnalyticsDashboardNavItem(filteredNavigation)
			maybeInsertConsentConfigApiNavItem(filteredNavigation)
			maybeFilterConsentCollectionByConfigKeys(filteredNavigation)
			maybeRedirectPrivacyPoliciesNavItem(filteredNavigation)
			maybeRedirectSiteScannerNavItem(filteredNavigation)
			maybeTransformExportsToBulkData(filteredNavigation)
			navBarItemsState.value = filteredNavigation
			labels.value = buildLabels(filteredNavigation)
			return filteredNavigation
		} finally {
			isLoading = false
		}
	})()
	return isLoadingPromise
}

export const clearNavbarItems = () => {
	navBarItemsState.value = null
}

export const useLabels = value => {
	if (value == null) return ''
	// convert to string
	let result = '' + value
	labels.value.forEach(({ text, displayText }) => {
		// if text is plural remove s
		if (text.endsWith('s')) {
			text = text.slice(0, -1)
		}
		if (value.includes(text)) {
			result = result.replace(text, displayText, 'g')
		}
		// match and replace lowercase
		if (value.includes(text.toLowerCase())) {
			result = result.replace(text.toLowerCase(), displayText.toLowerCase(), 'g')
		}

		if (result.endsWith('ys') && !result.toLowerCase().includes('keys')) {
			result = result.replace('ys', 'ies', 'g')
		}
	})
	return result
}
Vue.filter('useLabels', useLabels)

export const waitForNavBarItemsLoad = async reload => {
	if (isLoadingPromise === null || navBarItemsState.value == null || reload) {
		load()
	}
	return isLoadingPromise
}

export const navBarItems = computed(() => {
	if (navBarItemsState.value === null) {
		load()
		return {}
	}
	return navBarItemsState.value
})

export const externalLinks = computed(() => externalLinksState.value || [])

export const navBarItemsTest = computed(() => navBarItems.value || {})
